import React from 'react';
import { Check, Camera, ChevronRight } from 'lucide-react';

const t = {
  en: {
    label: 'AI TECHNOLOGY',
    title: 'AI NUTRITION SCANNER FOR ATHLETES',
    desc: 'Scan your meal — AI instantly calculates your protein, carb, and fat intake.',
    items: [
      'Instant protein & macro breakdown',
      'Track daily strength training nutrition goals',
      'AI meal rating for optimal muscle building',
    ],
    cta: 'Start scanner',
  },
  de: {
    label: 'KI TECHNOLOGIE',
    title: 'KI-ERNÄHRUNGSSCANNER FÜR ATHLETEN',
    desc: 'Scanne deine Mahlzeit — die KI berechnet sofort deine Protein-, Kohlenhydrat- und Fettzufuhr.',
    items: [
      'Sofortige Protein & Makro-Aufschlüsselung',
      'Verfolge tägliche Kraftsport-Ernährungsziele',
      'KI-Bewertung der Mahlzeit für optimalen Muskelaufbau',
    ],
    cta: 'Scanner starten',
  },
};

export default function FoodScanner({ lang }) {
  const tx = t[lang];

  return (
    <section
      className="py-24 px-6 relative overflow-hidden"
      style={{
        background: 'linear-gradient(180deg, #0f1117 0%, #111827 50%, #0f1117 100%)',
      }}
    >
      {/* Background glow */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
        style={{
          width: 800,
          height: 500,
          background: 'radial-gradient(ellipse, rgba(59,130,246,0.06) 0%, transparent 70%)',
        }}
      />

      <div className="max-w-6xl mx-auto relative z-10">
        <div className="grid md:grid-cols-2 gap-16 items-center">
          {/* Left: Content */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div
                className="flex items-center justify-center rounded-lg"
                style={{
                  width: 36,
                  height: 36,
                  background: 'linear-gradient(135deg, #3b82f6, #06b6d4)',
                }}
              >
                <Camera size={18} color="#ffffff" strokeWidth={2} />
              </div>
              <p
                className="text-sm font-bold uppercase"
                style={{ color: '#3b82f6', letterSpacing: '0.2em' }}
              >
                {tx.label}
              </p>
            </div>

            <h2
              className="font-black uppercase leading-tight mb-6"
              style={{
                fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
                letterSpacing: '-0.02em',
                color: '#ffffff',
              }}
            >
              {tx.title}
            </h2>

            <p
              className="mb-10 leading-relaxed"
              style={{ color: '#94a3b8', fontSize: '1.05rem' }}
            >
              {tx.desc}
            </p>

            <ul className="space-y-4 mb-10">
              {tx.items.map((item, i) => (
                <li key={i} className="flex items-start gap-4">
                  <div
                    className="flex-shrink-0 flex items-center justify-center rounded-full mt-0.5"
                    style={{
                      width: 28,
                      height: 28,
                      background: 'linear-gradient(135deg, #3b82f6, #06b6d4)',
                    }}
                  >
                    <Check size={15} color="#ffffff" strokeWidth={3} />
                  </div>
                  <span
                    className="leading-relaxed font-medium"
                    style={{ color: '#cbd5e1', fontSize: '1.02rem' }}
                  >
                    {item}
                  </span>
                </li>
              ))}
            </ul>

            <a
              href="/s"
              className="inline-flex items-center gap-3 px-7 py-3.5 rounded-lg font-bold uppercase transition-all duration-200 hover:gap-5 group"
              style={{
                background: 'linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)',
                color: '#ffffff',
                letterSpacing: '0.08em',
                fontSize: '0.9rem',
                boxShadow: '0 0 30px rgba(59,130,246,0.35)',
              }}
            >
              {tx.cta}
              <ChevronRight size={18} className="transition-transform group-hover:translate-x-1" />
            </a>
          </div>

          {/* Right: Image */}
          <div className="relative">
            <div
              className="rounded-2xl overflow-hidden"
              style={{ border: '1px solid rgba(59,130,246,0.2)' }}
            >
              <img
                src="https://images.unsplash.com/photo-1498837167922-ddd27525d352?w=800&q=80"
                alt="Food Scanner"
                className="w-full h-96 object-cover"
                style={{ objectPosition: 'center' }}
              />
              {/* Scan overlay effect */}
              <div
                className="absolute inset-0"
                style={{
                  background:
                    'linear-gradient(to bottom, rgba(15,17,23,0.1) 0%, rgba(59,130,246,0.05) 50%, rgba(15,17,23,0.3) 100%)',
                }}
              />
            </div>

            {/* Floating AI badge */}
            <div
              className="absolute -top-4 -right-4 rounded-xl px-4 py-3 hidden md:flex items-center gap-3"
              style={{
                background: 'linear-gradient(135deg, #1e3a6e, #1a2d5a)',
                border: '1px solid rgba(59,130,246,0.4)',
                boxShadow: '0 0 30px rgba(59,130,246,0.25)',
              }}
            >
              <div
                className="w-2 h-2 rounded-full animate-pulse"
                style={{ backgroundColor: '#06b6d4' }}
              />
              <span className="text-white font-bold text-sm">AI</span>
              <span style={{ color: '#64748b', fontSize: '0.8rem' }}>
                {lang === 'de' ? 'Aktiv' : 'Active'}
              </span>
            </div>

            {/* Floating nutrition card */}
            <div
              className="absolute -bottom-4 -left-4 rounded-xl px-4 py-3 hidden md:block"
              style={{
                background: 'rgba(15,17,23,0.9)',
                border: '1px solid rgba(59,130,246,0.25)',
                backdropFilter: 'blur(8px)',
              }}
            >
              <div className="text-xs font-semibold uppercase mb-2" style={{ color: '#64748b', letterSpacing: '0.1em' }}>
                {lang === 'de' ? 'Analyse' : 'Analysis'}
              </div>
              <div className="flex gap-4">
                {[
                  { val: '420', unit: 'kcal' },
                  { val: '32g', unit: lang === 'de' ? 'Prot.' : 'Prot.' },
                  { val: '18g', unit: 'Carbs' },
                ].map((s, i) => (
                  <div key={i} className="text-center">
                    <div className="text-white font-black text-sm">{s.val}</div>
                    <div className="text-xs" style={{ color: '#3b82f6' }}>{s.unit}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
